"use client";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { getFeaturedProperties } from "@/lib/property-service";
import { Property } from "@/types/types";
import PropertyCard from "@/components/listings/property-card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export default function FeaturedListings() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadProperties() {
      try {
        const data = await getFeaturedProperties(5);
        setProperties(data);
      } catch (error) {
        console.warn("Failed to fetch featured properties:", error);
        setProperties([]);
      } finally {
        setIsLoading(false);
      }
    }
    loadProperties();
  }, []);

  if (isLoading) {
    return (
      <section>
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-9 w-[250px]" />
          <Skeleton className="h-5 w-[100px]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} className="h-[300px] w-full" />
            ))}
        </div>
      </section>
    );
  }

  if (properties.length === 0) {
    return null;
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold">Featured Properties</h2>
        <Link to="/properties" className="text-primary hover:underline">
          View all
        </Link>
      </div>

      <Carousel className="w-full">
        <CarouselContent>
          {properties.map((property: Property) => (
            <CarouselItem
              key={property.id}
              className="md:basis-1/2 lg:basis-1/3 xl:basis-1/4"
            >
              <div className="relative">
                <Badge className="absolute top-2 left-2 z-10 bg-black/60 text-white border-transparent backdrop-blur-sm shadow-sm">
                  Featured
                </Badge>
                <PropertyCard listing={property} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-2" />
        <CarouselNext className="right-2" />
      </Carousel>
    </section>
  );
}
